<script setup>
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-389: one feedback row, read-only — the finding as the appraiser saw it,
 * and their answer. The link opens that check in the admin's read-only view
 * (`admin.report-qc.check`), for any appraiser; the evaluation and the
 * appraiser's quality-check pages stay owner-only.
 *
 * RED-361: `Ava küsimus <key>` opens that question on Kontrollnimekiri (its
 * item drawer), with the feedback's checklist version, so a `Vale` leads to
 * the text to fix in one click.
 */
const props = defineProps({
  row: { type: Object, default: null },
})

const visible = defineModel('visible', { type: Boolean, default: false })

const { t } = useI18n()

const finding = computed(() => props.row?.finding ?? null)

const chapter = computed(() => {
  const raw = finding.value?.location
  if (!raw) {
    return ''
  }
  if (typeof raw === 'string') {
    return raw
  }
  return [raw.chapter, raw.chapter_title].filter(Boolean).join(' · ')
})

/** The basis markers are a chip's job on the appraiser card; strip them here too (as QcFindingCard does). */
const basisLine = computed(() =>
  (finding.value?.basisText ?? '')
    .replace(/\[(?:standard|kinnitaja praktika)\]\s*/giu, '')
    .replace(/^[\s,;:.]+/u, '')
    .trim(),
)

// `v12` → 12; left out when no saved version carries the run's hash.
const questionParams = computed(() => {
  const version = Number(props.row?.version?.slice(1)) || null

  return { part: 'kontrollnimekiri', query: props.row?.questionKey, ...(version ? { version } : {}) }
})

const dateTime = iso =>
  iso ? new Intl.DateTimeFormat('et-EE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : ''
</script>

<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :header="t('messages.admin.report_qc.feedback.drawerTitle')"
    class="w-full! md:w-[32rem]!"
  >
    <div v-if="row && finding" class="flex flex-col gap-5 text-sm" data-testid="feedback-drawer">
      <section>
        <p class="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">
          {{ t('messages.admin.report_qc.feedback.drawerFinding') }}
        </p>
        <p class="font-medium text-gray-800" data-testid="feedback-drawer-title">{{ finding.title }}</p>
        <p v-if="row.questionKey" class="mt-0.5 text-xs text-gray-500">
          <span class="font-mono">{{ row.questionKey }}</span>
          <template v-if="row.questionTitle"> · {{ row.questionTitle }}</template>
          <template v-if="row.checkpoints.length">
            · {{ t('messages.admin.report_qc.feedback.checkpoint', { n: row.checkpoints.join(', ') }, row.checkpoints.length) }}
          </template>
        </p>
        <Link
          v-if="row.questionKey"
          :href="route('admin.report-qc.index', questionParams)"
          class="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 underline underline-offset-2 hover:text-blue-800"
          data-testid="feedback-drawer-question"
        ><i class="pi pi-pencil text-xs" aria-hidden="true" />{{ t('messages.admin.report_qc.feedback.openQuestion', { key: row.questionKey }) }}</Link>
        <p v-if="finding.problem" class="mt-2 whitespace-pre-line text-gray-700">{{ finding.problem }}</p>
        <p v-if="chapter || finding.excerptLine" class="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600">
          <span v-if="chapter" class="block font-sans font-semibold text-gray-500">{{ chapter }}</span>
          <span v-if="finding.excerptLine">{{ finding.excerptLine }}</span>
        </p>
        <p
          v-if="finding.fixSuggestion"
          class="mt-2 whitespace-pre-line rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-blue-700"
        >{{ finding.fixSuggestion }}</p>
        <p v-if="basisLine" class="mt-2 text-xs text-gray-500">{{ basisLine }}</p>
      </section>

      <section class="border-t border-gray-100 pt-4">
        <p class="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">
          {{ t('messages.admin.report_qc.feedback.drawerFeedback') }}
        </p>
        <p
          class="inline-flex items-center gap-1 font-medium"
          :class="row.verdict === 'vale' ? 'text-red-600' : 'text-emerald-600'"
          data-testid="feedback-drawer-verdict"
        >
          <span class="pi text-xs" :class="row.verdict === 'vale' ? 'pi-times' : 'pi-check'" aria-hidden="true"></span>
          {{ t(`messages.admin.report_qc.feedback.${row.verdict}`) }}
        </p>
        <p v-if="row.note" class="mt-1 whitespace-pre-line text-gray-700" data-testid="feedback-drawer-note">{{ row.note }}</p>
        <p v-else class="mt-1 text-gray-400">{{ t('messages.admin.report_qc.feedback.noNote') }}</p>
        <p class="mt-2 text-xs text-gray-500">
          {{ [row.appraiser, row.company, dateTime(row.at), row.version].filter(Boolean).join(' · ') }}
        </p>
      </section>

      <section class="border-t border-gray-100 pt-4">
        <p v-if="row.address" class="mb-2 text-gray-700">{{ row.address }}</p>
        <Link
          :href="route('admin.report-qc.check', { reportCheck: row.checkId, from: 'tagasiside' })"
          class="text-sm text-blue-600 underline underline-offset-2 hover:text-blue-800"
          data-testid="feedback-drawer-open"
        >{{ t('messages.admin.report_qc.feedback.open') }}</Link>
      </section>
    </div>
  </Drawer>
</template>
